import React, { useState } from 'react';
import { Icons } from './Icons';
import { useTree, useTreePeople } from '../contexts/TreeContext';
import { ordinal, byName, deriveMarriages } from '../utils/helpers';
import { track } from '../utils/analytics';

const selectStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: '1.5px solid #D5CAB8', background: '#fff',
  fontSize: 13, color: '#2C2418', fontFamily: "'DM Sans', sans-serif",
  outline: 'none', boxSizing: 'border-box',
};

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: '1.5px solid #D5CAB8', background: '#fff',
  fontSize: 13, color: '#2C2418', outline: 'none',
  fontFamily: "'DM Sans', sans-serif", boxSizing: 'border-box',
};

const labelStyle = {
  fontSize: 12, fontWeight: 600, color: '#6B5E4F',
  display: 'block', marginBottom: 6,
  textTransform: 'uppercase', letterSpacing: 0.8,
};

export default function EditRelationshipsModal({ personId, onClose }) {
  const { batchUpdatePeople } = useTree();
  const { people } = useTreePeople();
  const person = people[personId];
  const [saving, setSaving] = useState(false);

  // Seeded from both sides of each marriage, not just this person's own array.
  // A marriage recorded only on the spouse's document is listed on the
  // Relationships tab that opens this modal, so seeding from the raw array
  // showed "No marriages recorded" over a person the tab had just said was
  // married. Saving from here also writes the missing half back, which repairs
  // the record rather than leaving it one-sided.
  const [parentIds, setParentIds] = useState(person?.parentIds || []);
  const [marriages, setMarriages] = useState(
    () => deriveMarriages(personId, people).map(m => ({ ...m }))
  );

  if (!person) return null;

  const peopleList = Object.values(people).filter(p => p.id !== personId).sort(byName);

  const addMarriageSlot = () => {
    setMarriages([...marriages, {
      spouseId: '', marriageDate: '', marriagePlace: '',
      status: 'current', endReason: '', endDate: '', children: [],
    }]);
  };

  const updateMarriage = (idx, field, value) => {
    const copy = [...marriages];
    copy[idx] = { ...copy[idx], [field]: value };
    if (field === 'status' && value === 'current') {
      copy[idx].endReason = '';
      copy[idx].endDate = '';
    }
    setMarriages(copy);
  };

  const removeMarriage = (idx) => {
    setMarriages(marriages.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    setSaving(true);

    const oldParentIds = person.parentIds || [];
    const newParentIds = parentIds.filter(Boolean);
    // What the form was seeded with, not the raw array — removing a marriage
    // that was only stored on the spouse's document has to strip their copy,
    // and a raw `person.marriages` would not know the marriage existed.
    const oldMarriages = deriveMarriages(personId, people);
    const newMarriages = marriages.filter(m => m.spouseId || m.marriageDate);

    // Accumulate all writes into a map keyed by personId so each doc is written once
    const updatesMap = new Map();
    const merge = (id, data) => updatesMap.set(id, { ...(updatesMap.get(id) || {}), ...data });

    // Parent children-array changes
    for (const oldPid of oldParentIds) {
      if (!newParentIds.includes(oldPid) && people[oldPid]) {
        merge(oldPid, { children: (people[oldPid].children || []).filter(c => c !== personId) });
      }
    }
    for (const newPid of newParentIds) {
      if (!oldParentIds.includes(newPid) && people[newPid]) {
        merge(newPid, { children: [...new Set([...(people[newPid].children || []), personId])] });
      }
    }

    // Remove marriage from spouses no longer listed
    for (const oldM of oldMarriages) {
      const stillExists = newMarriages.find(m => m.spouseId === oldM.spouseId);
      if (!stillExists && oldM.spouseId && people[oldM.spouseId]) {
        merge(oldM.spouseId, {
          marriages: (people[oldM.spouseId].marriages || []).filter(m => m.spouseId !== personId),
        });
      }
    }

    // Add or update marriage on each spouse's record
    for (const newM of newMarriages) {
      if (!newM.spouseId || !people[newM.spouseId]) continue;
      // Use any already-pending marriages update for this spouse, otherwise start from their current data
      const baseMarriages = updatesMap.get(newM.spouseId)?.marriages
        ?? [...(people[newM.spouseId].marriages || [])];
      const existingIdx = baseMarriages.findIndex(m => m.spouseId === personId);
      const spouseMarriage = {
        spouseId: personId,
        marriageDate: newM.marriageDate || null,
        marriagePlace: newM.marriagePlace || null,
        status: newM.status,
        endReason: newM.status === 'ended' ? (newM.endReason || null) : null,
        endDate: newM.status === 'ended' ? (newM.endDate || null) : null,
        children: existingIdx >= 0 ? baseMarriages[existingIdx].children : [],
      };
      merge(newM.spouseId, {
        marriages: existingIdx >= 0
          ? baseMarriages.map((m, i) => i === existingIdx ? spouseMarriage : m)
          : [...baseMarriages, spouseMarriage],
      });
    }

    // Main person update
    merge(personId, {
      parentIds: newParentIds,
      marriages: newMarriages.map(m => ({
        spouseId: m.spouseId || null,
        marriageDate: m.marriageDate || null,
        marriagePlace: m.marriagePlace || null,
        status: m.status,
        endReason: m.status === 'ended' ? (m.endReason || null) : null,
        endDate: m.status === 'ended' ? (m.endDate || null) : null,
        children: m.children || [],
      })),
    });

    // Commit everything atomically in one batch
    await batchUpdatePeople(
      [...updatesMap.entries()].map(([id, data]) => ({ personId: id, data }))
    );

    // This screen edits a whole set of links at once, so what counts as an
    // addition is the diff, not the save: re-saving without changing anything
    // must produce no events. Removals are not measured — the plan asks how
    // people build a tree, and a detached parent answers a different question.
    for (const pid of newParentIds) {
      if (!oldParentIds.includes(pid)) track('relationship_added', { type: 'parent', via: 'edit_relationships' });
    }
    for (const m of newMarriages) {
      if (m.spouseId && !oldMarriages.some(o => o.spouseId === m.spouseId)) {
        track('relationship_added', { type: 'spouse', via: 'edit_relationships' });
      }
    }

    setSaving(false);
    onClose();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(44,36,24,0.5)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(4px)',
    }}>
      <div style={{
        width: 560, maxHeight: '88vh', background: '#FDFBF7', borderRadius: 16,
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden',
        fontFamily: "'DM Sans', sans-serif",
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #E8E0D4',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: '#F5EDE3',
        }}>
          <h3 style={{ margin: 0, fontSize: 18, fontFamily: "'Crimson Pro', Georgia, serif", color: '#2C2418', fontWeight: 700 }}>
            Edit Relationships — {person.name}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8B7D6B' }}>
            <Icons.Close />
          </button>
        </div>

        {/* Form body */}
        <div style={{ padding: 24, overflowY: 'auto', maxHeight: 'calc(88vh - 140px)' }}>

          {/* Parents */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              Parents
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              <select
                value={parentIds[0] || ''}
                onChange={e => { const v = [...parentIds]; v[0] = e.target.value; setParentIds(v); }}
                style={selectStyle}
              >
                <option value="">— Parent 1 —</option>
                {peopleList.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <select
                value={parentIds[1] || ''}
                onChange={e => { const v = [...parentIds]; v[1] = e.target.value; setParentIds(v); }}
                style={selectStyle}
              >
                <option value="">— Parent 2 —</option>
                {peopleList.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>

          {/* Marriages */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h4 style={{ margin: 0, fontSize: 12, color: '#8B7D6B', textTransform: 'uppercase', letterSpacing: 1.2, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icons.Ring /> Marriages & Partnerships
              </h4>
              <button onClick={addMarriageSlot} style={{
                background: 'rgba(196,151,90,0.12)', border: 'none', borderRadius: 6,
                padding: '5px 12px', color: '#C4975A', fontSize: 12, fontWeight: 600,
                cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                <Icons.Plus /> Add Marriage
              </button>
            </div>

            {marriages.map((m, idx) => (
              <div key={idx} style={{
                background: '#FAF7F2', borderRadius: 12, border: '1px solid #EBE4D8',
                padding: 16, marginBottom: 10,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#C4975A', textTransform: 'uppercase' }}>
                    {marriages.length > 1 ? `${ordinal(idx + 1)} Marriage` : 'Marriage'}
                  </span>
                  <button onClick={() => removeMarriage(idx)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', color: '#C0392B', fontSize: 11, fontWeight: 600,
                  }}>Remove</button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle}>Spouse</label>
                    <select value={m.spouseId} onChange={e => updateMarriage(idx, 'spouseId', e.target.value)} style={selectStyle}>
                      <option value="">— Select person —</option>
                      {peopleList.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>

                  <div>
                    <label style={labelStyle}>Marriage Date</label>
                    <input value={m.marriageDate || ''} onChange={e => updateMarriage(idx, 'marriageDate', e.target.value)} placeholder="YYYY-MM-DD" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Marriage Place</label>
                    <input value={m.marriagePlace || ''} onChange={e => updateMarriage(idx, 'marriagePlace', e.target.value)} placeholder="City, State" style={inputStyle} />
                  </div>

                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle}>Status</label>
                    <div style={{ display: 'flex', gap: 10 }}>
                      {['current', 'ended'].map(s => (
                        <button key={s} onClick={() => updateMarriage(idx, 'status', s)} style={{
                          flex: 1, padding: '9px 0', borderRadius: 8,
                          border: m.status === s ? '2px solid #C4975A' : '1.5px solid #D5CAB8',
                          background: m.status === s ? '#F5EDE3' : '#fff',
                          color: m.status === s ? '#C4975A' : '#6B5E4F',
                          fontSize: 13, fontWeight: 600, cursor: 'pointer',
                          fontFamily: "'DM Sans', sans-serif",
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        }}>
                          {s === 'current' ? <><Icons.Heart /> Current</> : <><Icons.Unlink /> Ended</>}
                        </button>
                      ))}
                    </div>
                  </div>

                  {m.status === 'ended' && (
                    <>
                      <div style={{ gridColumn: 'span 2' }}>
                        <label style={labelStyle}>How did this marriage end?</label>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {[
                            { value: 'divorce', label: 'Divorce', color: '#BF6C00', bg: '#FFF3E0' },
                            { value: 'death', label: 'Death of Spouse', color: '#8B7D6B', bg: '#F0EBE3' },
                            { value: 'annulment', label: 'Annulment', color: '#C0392B', bg: '#FDE8E8' },
                            { value: 'separation', label: 'Legal Separation', color: '#9A7D0A', bg: '#FFF8E1' },
                          ].map(r => (
                            <button key={r.value} onClick={() => updateMarriage(idx, 'endReason', r.value)} style={{
                              padding: '7px 14px', borderRadius: 8,
                              border: m.endReason === r.value ? `2px solid ${r.color}` : '1.5px solid #D5CAB8',
                              background: m.endReason === r.value ? r.bg : '#fff',
                              color: m.endReason === r.value ? r.color : '#6B5E4F',
                              fontSize: 12, fontWeight: 600, cursor: 'pointer',
                              fontFamily: "'DM Sans', sans-serif",
                            }}>{r.label}</button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label style={labelStyle}>Date Ended</label>
                        <input value={m.endDate || ''} onChange={e => updateMarriage(idx, 'endDate', e.target.value)} placeholder="YYYY-MM-DD" style={inputStyle} />
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}

            {marriages.length === 0 && (
              <p style={{ fontSize: 12, color: '#A0937F', textAlign: 'center', padding: '8px 0' }}>
                No marriages recorded — click "+ Add Marriage" to add one
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid #E8E0D4',
          display: 'flex', justifyContent: 'flex-end', gap: 10,
          background: '#FDFBF7',
        }}>
          <button onClick={onClose} style={{
            padding: '10px 24px', borderRadius: 8, border: '1.5px solid #D5CAB8',
            background: '#fff', color: '#6B5E4F', fontSize: 13, fontWeight: 600,
            cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
          }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{
            padding: '10px 28px', borderRadius: 8, border: 'none',
            background: saving ? '#B8AFA0' : '#3B2F1E',
            color: '#F5EDE3', fontSize: 13, fontWeight: 600,
            cursor: saving ? 'wait' : 'pointer', fontFamily: "'DM Sans', sans-serif",
          }}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
}
